/**
 * brioCAST Venue Creatives Page (MVP, media_url only)
 *
 * Wired in App.jsx renderPage():
 *   case "venue-creatives": return hasCap(account, "SCREEN_NETWORK")
 *     ? <VenueCreativesPage account={account} /> : <NoCap cap="SCREEN_NETWORK" />;
 *
 * Sidebar group: "場域與設備"
 *   { id: "venue-creatives", label: "場域素材", icon: "🎬", cap: "SCREEN_NETWORK" }
 *
 * Scope:
 *   - List / filter / create / edit / submit / archive / preview
 *   - No real file upload, no S3, no CDN — media_url only
 */
import { useCallback, useEffect, useMemo, useState } from "react";

const API_ROOT = "https://api.briolabs.io";

async function vcApi(path, options = {}) {
  const token = localStorage.getItem("biz_token");
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${API_ROOT}${path}`, {
    ...options,
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  let data = null;
  try { data = await res.json(); } catch (_) { data = null; }
  if (!res.ok) {
    const err = new Error((data && (data.error || data.message)) || `Request failed (${res.status})`);
    err.status = res.status;
    err.payload = data;
    throw err;
  }
  return data;
}

function newIdempotencyKey() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  // Fallback only — RFC4122-ish without external deps.
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Response-shape adapters for the briocast-api venue-creatives contract.
// LIST  → { success, creatives: [...], next_cursor }
// ITEM  → { success, creative: {...} }   (also returned by POST/PATCH/state transitions)
// Fallbacks tolerate prior {data}/{items}/bare-array shapes during rollout.
// API uses approval_status; portal uses status everywhere. Alias on ingestion
// so the rest of the page keeps working without 10+ call-site changes.
function normalizeCreative(row) {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    status: row.status ?? row.approval_status,
  };
}

function unwrapList(res) {
  const arr = Array.isArray(res?.creatives) ? res.creatives
    : Array.isArray(res?.items) ? res.items
    : Array.isArray(res?.data) ? res.data
    : Array.isArray(res) ? res
    : [];
  return arr.map(normalizeCreative);
}


function unwrapCreative(res) {
  const raw = res?.creative ?? res?.data ?? res ?? null;
  return raw ? normalizeCreative(raw) : null;
}


const t = {
  bg: "#0F1117", card: "#1A1D27", cardHover: "#22262F", border: "#2A2E3A",
  accent: "#6C5CE7", accentLight: "#A29BFE",
  success: "#00B894", warning: "#FDCB6E", danger: "#E17055",
  text: "#E8E8ED", textMuted: "#8B8FA3", textDim: "#5A5E72",
};

const STATUS_META = {
  draft:          { label: "草稿",   color: t.textMuted, bg: "#2A2E3A" },
  pending_review: { label: "待審核", color: t.warning,   bg: "#3A2F1A" },
  approved:       { label: "已核准", color: t.success,   bg: "#0F2E26" },
  rejected:       { label: "已退回", color: t.danger,    bg: "#3A1F1A" },
  archived:       { label: "已封存", color: t.textDim,   bg: "#1F2128" },
};

const STATUS_FILTERS = [
  { value: "all",            label: "全部" },
  { value: "draft",          label: "草稿" },
  { value: "pending_review", label: "待審核" },
  { value: "approved",       label: "已核准" },
  { value: "rejected",       label: "已退回" },
  { value: "archived",       label: "已封存" },
];

const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "video/mp4"];

const EMPTY_FORM = {
  venue_id: "",
  zone_id: "",
  creative_type: "image",
  title: "",
  caption: "",
  cta_text: "",
  cta_url: "",
  qr_url: "",
  media_url: "",
  media_mime_type: "image/jpeg",
  media_size_bytes: "",
  media_width: "",
  media_height: "",
  duration_ms: 8000,
  starts_at: "",
  ends_at: "",
};

// ============================================================================
// UI helpers
// ============================================================================
function StatusChip({ status }) {
  const meta = STATUS_META[status] || { label: status, color: t.textMuted, bg: "#2A2E3A" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 20,
      background: meta.bg, color: meta.color, fontSize: 11, fontWeight: 600,
      border: `1px solid ${meta.color}33`,
    }}>{meta.label}</span>
  );
}

function FieldError({ msg }) {
  if (!msg) return null;
  return <div style={{ fontSize: 11, color: t.danger, marginTop: 4 }}>{msg}</div>;
}

function WarningBanner() {
  return (
    <div style={{
      padding: "10px 14px", borderRadius: 10,
      background: "#3A2F1A", border: `1px solid ${t.warning}55`,
      color: t.warning, fontSize: 12, fontWeight: 500,
      marginBottom: 16, display: "flex", alignItems: "center", gap: 8,
    }}>
      <span>⚠️</span>
      <span>目前使用 media URL；檔案上傳與 CDN 儲存將於下一階段提供。</span>
    </div>
  );
}

// Convert a backend 422 errors payload into a flat {field: message} map.
function normalizeErrors(payload) {
  if (!payload) return {};
  const errs = payload.errors;
  if (!errs) return {};
  if (Array.isArray(errs)) {
    const out = {};
    for (const e of errs) {
      const key = e.field || e.path || e.param || "_root";
      out[key] = e.message || e.msg || String(e);
    }
    return out;
  }
  if (typeof errs === "object") {
    const out = {};
    for (const [k, v] of Object.entries(errs)) {
      out[k] = Array.isArray(v) ? v.join("、") : String(v);
    }
    return out;
  }
  return { _root: String(errs) };
}

// ============================================================================
// Client-side validation
// ============================================================================
function validateForm(form) {
  const errs = {};
  const title = (form.title || "").trim();
  if (!title) errs.title = "標題為必填";
  else if (title.length > 120) errs.title = "標題不可超過 120 字";

  if ((form.caption || "").length > 280) errs.caption = "說明不可超過 280 字";
  if ((form.cta_text || "").length > 32) errs.cta_text = "CTA 文字不可超過 32 字";

  const httpsOk = (v) => !v || /^https:\/\//i.test(v);
  if (!form.media_url) errs.media_url = "media URL 為必填";
  else if (!httpsOk(form.media_url)) errs.media_url = "media URL 必須以 https:// 開頭";

  if (form.cta_url && !httpsOk(form.cta_url)) errs.cta_url = "CTA URL 必須以 https:// 開頭";
  if (form.qr_url && !httpsOk(form.qr_url)) errs.qr_url = "QR URL 必須以 https:// 開頭";

  if (!ALLOWED_MIME.includes(form.media_mime_type)) {
    errs.media_mime_type = "不支援的 MIME 類型";
  }

  const dur = Number(form.duration_ms);
  if (!Number.isFinite(dur)) {
    errs.duration_ms = "請輸入有效的播放秒數";
  } else if (form.creative_type === "image" && (dur < 5000 || dur > 30000)) {
    errs.duration_ms = "圖片素材播放長度需在 5000–30000 ms 之間";
  } else if (form.creative_type === "video" && (dur < 5000 || dur > 60000)) {
    errs.duration_ms = "影片素材播放長度需在 5000–60000 ms 之間";
  }

  if (form.starts_at && form.ends_at) {
    const s = new Date(form.starts_at).getTime();
    const e = new Date(form.ends_at).getTime();
    if (Number.isFinite(s) && Number.isFinite(e) && e <= s) {
      errs.ends_at = "結束時間必須晚於開始時間";
    }
  }
  return errs;
}

// Build a payload that drops empty optionals and coerces numbers.
// Create requires API-owned constants: source_type=venue_owned and layout=SponsoredCard.
// Patch must not include those create-only fields because the API only allows patchable fields.
function buildPayload(form, mode = "create") {
  const num = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));
  const str = (v) => (v && String(v).trim() ? String(v).trim() : undefined);
  const iso = (v) => (v ? new Date(v).toISOString() : undefined);

  const payload = {
    venue_id: str(form.venue_id),
    zone_id: str(form.zone_id),
    creative_type: form.creative_type,
    title: str(form.title),
    caption: str(form.caption),
    cta_text: str(form.cta_text),
    cta_url: str(form.cta_url),
    qr_url: str(form.qr_url),
    media_url: str(form.media_url),
    media_mime_type: form.media_mime_type,
    media_size_bytes: num(form.media_size_bytes),
    media_width: num(form.media_width),
    media_height: num(form.media_height),
    duration_ms: num(form.duration_ms),
    starts_at: iso(form.starts_at),
    ends_at: iso(form.ends_at),
  };

  if (mode === "create") {
    payload.source_type = "venue_owned";
    payload.layout = "SponsoredCard";
  }

  return payload;
}

// ============================================================================
// Preview card (mirrors SponsoredCard look)
// ============================================================================
function PreviewCard({ form }) {
  const isVideo = form.creative_type === "video";
  return (
    <div style={{
      background: "#0B0D14", borderRadius: 14,
      border: `1px solid ${t.border}`, overflow: "hidden",
      maxWidth: 320,
    }}>
      <div style={{
        position: "relative", width: "100%", aspectRatio: "16 / 9",
        background: "#000", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        {form.media_url ? (
          isVideo ? (
            <video
              src={form.media_url}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              muted playsInline controls
            />
          ) : (
            <img
              src={form.media_url}
              alt={form.title || "preview"}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          )
        ) : (
          <div style={{ color: t.textDim, fontSize: 12 }}>尚未提供 media URL</div>
        )}
        <span style={{
          position: "absolute", top: 8, left: 8,
          background: "rgba(0,0,0,0.7)", color: t.textMuted,
          fontSize: 10, padding: "2px 8px", borderRadius: 10, letterSpacing: 1,
        }}>SPONSORED</span>
      </div>
      <div style={{ padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.text }}>
            {form.title || "（標題預覽）"}
          </div>
          {form.status && <StatusChip status={form.status} />}
        </div>
        {form.caption && (
          <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
            {form.caption}
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {form.cta_text ? (
            <span style={{
              display: "inline-block", padding: "6px 14px", borderRadius: 8,
              background: t.accent, color: "#fff", fontSize: 12, fontWeight: 600,
            }}>{form.cta_text}</span>
          ) : <span />}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {form.qr_url && <span title={form.qr_url} style={{ fontSize: 14, color: t.textMuted }}>🔳</span>}
            <span style={{ fontSize: 11, color: t.textDim }}>
              {(Number(form.duration_ms) || 0) / 1000}s
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Editor modal
// ============================================================================
const EDITABLE_KEYS = Object.keys(EMPTY_FORM);
function isDirty(form, snapshot) {
  if (!snapshot) return false;
  return EDITABLE_KEYS.some((k) => (form[k] ?? "") !== (snapshot[k] ?? ""));
}

function EditorModal({ open, mode, initial, onClose, onSaved, onSubmitted, onArchive }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [initialSnapshot, setInitialSnapshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [serverErr, setServerErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      const isoToLocal = (v) => {
        if (!v) return "";
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return "";
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
      };
      const loaded = {
        ...EMPTY_FORM,
        ...initial,
        media_size_bytes: initial.media_size_bytes ?? "",
        media_width: initial.media_width ?? "",
        media_height: initial.media_height ?? "",
        duration_ms: initial.duration_ms ?? 8000,
        starts_at: isoToLocal(initial.starts_at),
        ends_at: isoToLocal(initial.ends_at),
      };
      setForm(loaded);
      setInitialSnapshot(loaded);
    } else {
      setForm(EMPTY_FORM);
      setInitialSnapshot(null);
    }
    setErrors({});
    setServerErr("");
  }, [open, initial]);

  if (!open) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const v = validateForm(form);
    setErrors(v);
    if (Object.keys(v).length) return;
    setSaving(true); setServerErr("");
    try {
      const payload = buildPayload(form, mode);
      if (mode === "create") {
        const res = await vcApi("/api/venue-creatives", {
          method: "POST",
          headers: { "Idempotency-Key": newIdempotencyKey() },
          body: payload,
        });
        onSaved?.(unwrapCreative(res));
      } else {
        const res = await vcApi(`/api/venue-creatives/${initial.id}`, {
          method: "PATCH",
          body: payload,
        });
        onSaved?.(unwrapCreative(res));
      }
    } catch (e) {
      if (e.status === 422) {
        setErrors((prev) => ({ ...prev, ...normalizeErrors(e.payload) }));
        setServerErr("提交內容未通過後端驗證，請檢查欄位錯誤。");
      } else {
        setServerErr(e.message || "儲存失敗");
      }
    } finally {
      setSaving(false);
    }
  };

  const submitForReview = async () => {
    if (!initial?.id) return;
    setSubmitting(true); setServerErr("");
    const dirty = isDirty(form, initialSnapshot);
    try {
      // If the user typed unsaved edits in the modal, persist them via PATCH
      // before /submit — otherwise the server would submit a stale draft.
      // PATCH and /submit are distinct operations, so each gets its own
      // Idempotency-Key. A 422 on the PATCH aborts before /submit fires.
      if (dirty) {
        const v = validateForm(form);
        setErrors(v);
        if (Object.keys(v).length) {
          setServerErr("無法送審：請先修正欄位錯誤。");
          setSubmitting(false);
          return;
        }
        try {
          await vcApi(`/api/venue-creatives/${initial.id}`, {
            method: "PATCH",
            headers: { "Idempotency-Key": newIdempotencyKey() },
            body: buildPayload(form),
          });
        } catch (patchErr) {
          if (patchErr.status === 422) {
            setErrors((prev) => ({ ...prev, ...normalizeErrors(patchErr.payload) }));
            setServerErr("提交內容未通過後端驗證，請修正欄位後再送審。");
            setSubmitting(false);
            return;
          }
          throw patchErr;
        }
      }
      const res = await vcApi(`/api/venue-creatives/${initial.id}/submit`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
      });
      onSubmitted?.(unwrapCreative(res), { savedFirst: dirty });
    } catch (e) {
      if (e.status === 422) {
        setErrors((prev) => ({ ...prev, ...normalizeErrors(e.payload) }));
        setServerErr("無法送審：請檢查欄位錯誤。");
      } else {
        setServerErr(e.message || "送審失敗");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const input = (label, key, props = {}) => (
    <div style={{ marginBottom: 12 }}>
      <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: "block", fontWeight: 500 }}>
        {label}
      </label>
      <input
        style={{
          width: "100%", padding: "10px 12px", background: "#141720",
          border: `1px solid ${errors[key] ? t.danger : t.border}`,
          borderRadius: 8, color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box",
        }}
        value={form[key] ?? ""}
        onChange={(e) => set(key, e.target.value)}
        {...props}
      />
      <FieldError msg={errors[key]} />
    </div>
  );

  // Approved + archived rows are server-side immutable from the partner portal;
  // every other status (draft / pending_review / rejected) stays editable.
  const isReadOnly = !!initial && ["approved", "archived"].includes(initial.status);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: t.card, borderRadius: 16, border: `1px solid ${t.border}`,
          width: "100%", maxWidth: 880, maxHeight: "92vh", overflow: "auto",
        }}
      >
        <div style={{
          padding: "18px 24px", borderBottom: `1px solid ${t.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h3 style={{ margin: 0, fontSize: 18, color: t.text }}>
            {mode === "create" ? "新增場域素材" : "編輯場域素材"}
          </h3>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {initial?.status && <StatusChip status={initial.status} />}
            <button onClick={onClose} style={{
              background: "transparent", border: "none", color: t.textMuted,
              fontSize: 20, cursor: "pointer",
            }}>✕</button>
          </div>
        </div>

        <div style={{ padding: 24, display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 24 }}>
          <div>
            <WarningBanner />
            {serverErr && (
              <div style={{
                padding: "10px 14px", borderRadius: 10, marginBottom: 14,
                background: "#3A1F1A", border: `1px solid ${t.danger}55`,
                color: t.danger, fontSize: 12,
              }}>{serverErr}</div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {input("Venue ID", "venue_id", { placeholder: "ven_xxx" })}
              {input("Zone ID",  "zone_id",  { placeholder: "zone_xxx" })}
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: "block", fontWeight: 500 }}>素材類型</label>
              <select
                value={form.creative_type}
                onChange={(e) => set("creative_type", e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", background: "#141720",
                  border: `1px solid ${t.border}`, borderRadius: 8, color: t.text, fontSize: 13,
                }}
              >
                <option value="image">圖片 (image)</option>
                <option value="video">影片 (video)</option>
              </select>
            </div>

            {input("標題 *", "title", { maxLength: 120, placeholder: "最多 120 字" })}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: "block", fontWeight: 500 }}>說明 (caption)</label>
              <textarea
                value={form.caption}
                onChange={(e) => set("caption", e.target.value)}
                maxLength={280}
                rows={3}
                style={{
                  width: "100%", padding: "10px 12px", background: "#141720",
                  border: `1px solid ${errors.caption ? t.danger : t.border}`, borderRadius: 8,
                  color: t.text, fontSize: 13, outline: "none", resize: "vertical", boxSizing: "border-box",
                }}
              />
              <FieldError msg={errors.caption} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
              {input("CTA 文字", "cta_text", { maxLength: 32 })}
              {input("CTA 連結 (https://)", "cta_url", { placeholder: "https://..." })}
            </div>

            {input("QR 連結 (https://)", "qr_url", { placeholder: "https://..." })}
            {input("Media URL * (https://)", "media_url", { placeholder: "https://cdn.example.com/asset.mp4" })}

            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: t.textMuted, marginBottom: 4, display: "block", fontWeight: 500 }}>Media MIME 類型</label>
              <select
                value={form.media_mime_type}
                onChange={(e) => set("media_mime_type", e.target.value)}
                style={{
                  width: "100%", padding: "10px 12px", background: "#141720",
                  border: `1px solid ${errors.media_mime_type ? t.danger : t.border}`,
                  borderRadius: 8, color: t.text, fontSize: 13,
                }}
              >
                {ALLOWED_MIME.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <FieldError msg={errors.media_mime_type} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {input("檔案大小 (bytes)", "media_size_bytes", { type: "number", min: 0 })}
              {input("寬 (px)", "media_width", { type: "number", min: 0 })}
              {input("高 (px)", "media_height", { type: "number", min: 0 })}
            </div>

            {input("播放長度 (ms) *", "duration_ms", { type: "number", min: 5000, max: 60000 })}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {input("開始時間", "starts_at", { type: "datetime-local" })}
              {input("結束時間", "ends_at", { type: "datetime-local" })}
            </div>

            {isReadOnly && (
              <div style={{
                padding: "10px 14px", borderRadius: 10, marginTop: 8,
                background: "#1F2128", border: `1px solid ${t.border}`,
                color: t.textMuted, fontSize: 12,
              }}>此狀態下的素材無法在前端編輯；如需修改請與管理員協調。</div>
            )}
          </div>

          <div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 8, fontWeight: 600, letterSpacing: 1 }}>
              預覽 (SponsoredCard)
            </div>
            <PreviewCard form={{ ...form, status: initial?.status }} />
          </div>
        </div>

        <div style={{
          padding: "16px 24px", borderTop: `1px solid ${t.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div style={{ fontSize: 11, color: t.textDim }}>
            {mode === "edit" && initial?.id ? `ID: ${initial.id}` : "尚未建立"}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={onClose} style={{
              padding: "10px 18px", borderRadius: 8, border: `1px solid ${t.border}`,
              background: "transparent", color: t.textMuted, fontSize: 13, cursor: "pointer",
            }}>取消</button>
            {mode === "edit" && isReadOnly && initial?.status !== "archived" && initial?.id && (
              <button
                onClick={() => onArchive?.(initial)}
                style={{
                  padding: "10px 18px", borderRadius: 8,
                  border: `1px solid ${t.danger}`, background: "transparent",
                  color: t.danger, fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >封存</button>
            )}
            {mode === "edit" && !isReadOnly && (
              <button
                onClick={submitForReview}
                disabled={submitting || saving}
                style={{
                  padding: "10px 18px", borderRadius: 8, border: "none",
                  background: t.warning, color: "#000",
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  opacity: (submitting || saving) ? 0.6 : 1,
                }}
              >{submitting ? "送審中..." : "送出審核"}</button>
            )}
            {!isReadOnly && (
              <button
                onClick={save}
                disabled={saving || submitting}
                style={{
                  padding: "10px 18px", borderRadius: 8, border: "none",
                  background: t.accent, color: "#fff",
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                  opacity: (saving || submitting) ? 0.6 : 1,
                }}
              >{saving ? "儲存中..." : mode === "create" ? "建立草稿" : "儲存變更"}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Page
// ============================================================================
export default function VenueCreativesPage({ account }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("create");
  const [editorInitial, setEditorInitial] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState("");

  // Fetch the unfiltered dataset once: chip counts must always reflect the
  // full set (filtering server-side would make every chip show the active
  // status's count). The active filter narrows the table client-side via
  // `filtered` below. If the list ever grows large enough that this matters,
  // revisit with server-side pagination + a separate counts endpoint.
  const loadList = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await vcApi(`/api/venue-creatives`);
      setItems(unwrapList(res));
    } catch (e) {
      setError(e.message || "載入失敗");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const openCreate = () => {
    setEditorMode("create");
    setEditorInitial(null);
    setEditorOpen(true);
  };

  const openEdit = async (row) => {
    setEditorMode("edit");
    try {
      const res = await vcApi(`/api/venue-creatives/${row.id}`);
      setEditorInitial(unwrapCreative(res) || row);
    } catch (_) {
      setEditorInitial(row);
    }
    setEditorOpen(true);
  };

  const archive = async (row) => {
    if (!window.confirm(`確定要封存「${row.title || row.id}」嗎？`)) return;
    try {
      await vcApi(`/api/venue-creatives/${row.id}/archive`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
      });
      setToast("已封存");
      loadList();
    } catch (e) {
      setToast(e.message || "封存失敗");
    }
  };

  const submitFromRow = async (row) => {
    try {
      await vcApi(`/api/venue-creatives/${row.id}/submit`, {
        method: "POST",
        headers: { "Idempotency-Key": newIdempotencyKey() },
      });
      setToast("已送出審核");
      loadList();
    } catch (e) {
      if (e.status === 422) setToast("送審失敗：請先補齊欄位");
      else setToast(e.message || "送審失敗");
    }
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => it.status === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    for (const s of Object.keys(STATUS_META)) {
      c[s] = items.filter((it) => it.status === s).length;
    }
    return c;
  }, [items]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, color: t.text }}>🎬 場域素材</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: t.textMuted }}>
            管理場域自製廣告素材（venue-owned creatives）— 草稿、送審、封存與預覽。
          </p>
        </div>
        <button
          onClick={openCreate}
          style={{
            padding: "10px 18px", borderRadius: 8, border: "none",
            background: t.accent, color: "#fff",
            fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}
        >＋ 新增素材</button>
      </div>

      <div style={{ marginTop: 16 }}>
        <WarningBanner />
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {STATUS_FILTERS.map((f) => {
          const active = filter === f.value;
          const n = counts[f.value] ?? 0;
          return (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              style={{
                padding: "6px 14px", borderRadius: 20,
                background: active ? `${t.accent}33` : "transparent",
                color: active ? t.accentLight : t.textMuted,
                border: `1px solid ${active ? t.accent : t.border}`,
                fontSize: 12, fontWeight: 600, cursor: "pointer",
              }}
            >{f.label} <span style={{ opacity: 0.6, marginLeft: 4 }}>{n}</span></button>
          );
        })}
      </div>

      {error && (
        <div style={{
          padding: "10px 14px", borderRadius: 10, marginBottom: 14,
          background: "#3A1F1A", border: `1px solid ${t.danger}55`,
          color: t.danger, fontSize: 12,
        }}>{error}</div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: t.textMuted, fontSize: 13 }}>載入中...</div>
      ) : filtered.length === 0 ? (
        <div style={{
          textAlign: "center", padding: 48, color: t.textMuted, fontSize: 13,
          background: t.card, borderRadius: 12, border: `1px dashed ${t.border}`,
        }}>
          目前沒有符合條件的素材。
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {filtered.map((row) => (
            <div key={row.id} style={{
              background: t.card, border: `1px solid ${t.border}`,
              borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column",
            }}>
              <div style={{
                position: "relative", width: "100%", aspectRatio: "16 / 9",
                background: "#0B0D14", display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                {row.media_url && row.creative_type === "video" ? (
                  <video src={row.media_url} muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : row.media_url ? (
                  <img
                    src={row.media_url} alt={row.title || ""}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                  />
                ) : (
                  <div style={{ color: t.textDim, fontSize: 12 }}>無 media</div>
                )}
                <div style={{ position: "absolute", top: 8, right: 8 }}>
                  <StatusChip status={row.status} />
                </div>
              </div>
              <div style={{ padding: 12, flex: 1, display: "flex", flexDirection: "column" }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: t.text, marginBottom: 4 }}>
                  {row.title || "(未命名)"}
                </div>
                <div style={{ fontSize: 11, color: t.textDim, marginBottom: 8 }}>
                  {row.creative_type} · {(Number(row.duration_ms) || 0) / 1000}s
                  {row.zone_id ? ` · ${row.zone_id}` : ""}
                </div>
                {row.caption && (
                  <div style={{
                    fontSize: 12, color: t.textMuted, marginBottom: 10, lineHeight: 1.4,
                    overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box",
                    WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                  }}>{row.caption}</div>
                )}
                <div style={{ marginTop: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => openEdit(row)} style={btnSm()}>編輯</button>
                  {row.status === "draft" && (
                    <button onClick={() => submitFromRow(row)} style={btnSm(t.warning, "#000")}>送審</button>
                  )}
                  {row.status !== "archived" && (
                    <button onClick={() => archive(row)} style={btnSm("transparent", t.danger, t.danger)}>封存</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <EditorModal
        open={editorOpen}
        mode={editorMode}
        initial={editorInitial}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); setToast("已儲存"); loadList(); }}
        onSubmitted={(_res, opts) => {
          setEditorOpen(false);
          setToast(opts?.savedFirst ? "已自動儲存變更，已送出審核" : "已送出審核");
          loadList();
        }}
        onArchive={async (row) => { await archive(row); setEditorOpen(false); }}
      />

      {toast && (
        <div
          onClick={() => setToast(null)}
          style={{
            position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
            background: "#2A2035", border: `1px solid ${t.accent}`, borderRadius: 12,
            padding: "12px 22px", color: t.text, fontSize: 13, zIndex: 9999, cursor: "pointer",
          }}
        >{toast}</div>
      )}
    </div>
  );
}

function btnSm(bg, color, border) {
  return {
    padding: "6px 12px", borderRadius: 6,
    border: `1px solid ${border || (bg && bg !== "transparent" ? bg : t.border)}`,
    background: bg || `${t.accent}22`,
    color: color || t.accentLight,
    fontSize: 12, fontWeight: 600, cursor: "pointer",
  };
}
